import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Gift, Gem, Zap, Check, Flame } from 'lucide-react'
import { Modal, Button } from '@/components/ui'
import { useDailyRewardsStore, DAILY_REWARDS } from '@/stores/useDailyRewardsStore'
import { useUserStore } from '@/stores/useUserStore'
import { useAchievementsStore } from '@/stores/useAchievementsStore'
import { useSounds } from '@/lib/sounds'

interface DailyRewardsModalProps {
  isOpen: boolean
  onClose: () => void
}

export function DailyRewardsModal({ isOpen, onClose }: DailyRewardsModalProps) {
  const { canClaimToday, claimDailyReward, getStreakDay, currentStreak } = useDailyRewardsStore()
  const { addGems, addXp } = useUserStore()
  const { playFanfare, playCoin, playSuccess } = useSounds()

  const [claimed, setClaimed] = useState(false)
  const [claimedReward, setClaimedReward] = useState<{ gems: number; xp: number } | null>(null)

  const canClaim = canClaimToday()
  const currentDay = getStreakDay()

  useEffect(() => {
    // Reset claimed state when modal opens
    if (isOpen) {
      setClaimed(false)
      setClaimedReward(null)
    }
  }, [isOpen])

  const handleClaim = () => {
    const reward = claimDailyReward()
    if (reward) {
      addGems(reward.gems)
      addXp(reward.xp)

      // Track login streak achievement
      const newStreak = useDailyRewardsStore.getState().currentStreak
      useAchievementsStore.getState().updateStat('loginStreak', newStreak)

      if (reward.isMilestone) {
        playFanfare()
      } else {
        playCoin()
        playSuccess()
      }

      setClaimedReward({ gems: reward.gems, xp: reward.xp })
      setClaimed(true)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={!canClaim || claimed}
    >
      <div className="text-center py-2">
        {/* Header */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', damping: 12 }}
          className="w-20 h-20 mx-auto bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mb-4"
        >
          <Gift size={40} className="text-white" />
        </motion.div>

        <h2 className="font-display font-bold text-2xl mb-1">Daily Login</h2>

        {/* Streak indicator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <Flame className="text-orange-400" size={18} />
          <span className="text-text-secondary">
            {currentStreak > 0 ? `${currentStreak} day streak!` : 'Start your streak!'}
          </span>
        </div>

        {/* 7-day reward calendar */}
        <div className="grid grid-cols-7 gap-1.5 mb-6">
          {DAILY_REWARDS.map((reward, index) => {
            const dayNum = index + 1
            const isPast = dayNum < currentDay && canClaim
            const isCurrent = dayNum === currentDay
            const isClaimedToday = claimed && isCurrent

            return (
              <motion.div
                key={dayNum}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`relative p-2 rounded-lg text-center ${
                  isClaimedToday
                    ? 'bg-success/20 border-2 border-success'
                    : isCurrent && canClaim
                    ? 'bg-primary/20 border-2 border-primary animate-pulse'
                    : isPast || (!canClaim && dayNum === currentDay - 1)
                    ? 'bg-success/10 border border-success/30'
                    : 'bg-bg-dark border border-transparent'
                }`}
              >
                {/* Day number */}
                <p className={`text-[10px] font-bold mb-0.5 ${
                  isCurrent ? 'text-primary' : 'text-text-muted'
                }`}>
                  Day {dayNum}
                </p>

                {/* Reward icon */}
                <div className="text-lg mb-0.5">
                  {reward.isMilestone ? '🎁' : '💎'}
                </div>

                {/* Gem amount */}
                <p className={`text-[10px] font-bold ${
                  reward.isMilestone ? 'text-yellow-400' : 'text-gem'
                }`}>
                  +{reward.gems}
                </p>

                {/* Checkmark for claimed days */}
                {(isPast || isClaimedToday || (!canClaim && dayNum <= currentDay - 1)) && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center">
                    <Check size={10} className="text-white" />
                  </div>
                )}
              </motion.div>
            )
          })}
        </div>

        {/* Claim section */}
        <AnimatePresence mode="wait">
          {!claimed && canClaim ? (
            <motion.div
              key="claim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Today's reward preview */}
              <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-xl p-4 mb-4">
                <p className="text-sm text-text-secondary mb-2">Today's Reward</p>
                <div className="flex justify-center gap-6">
                  <div className="flex items-center gap-2">
                    <Gem className="text-gem" size={20} />
                    <span className="font-bold text-lg">+{DAILY_REWARDS[currentDay - 1].gems}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Zap className="text-accent" size={20} />
                    <span className="font-bold text-lg">+{DAILY_REWARDS[currentDay - 1].xp} XP</span>
                  </div>
                </div>
              </div>

              <Button onClick={handleClaim} size="lg" fullWidth>
                Claim Reward! 🎉
              </Button>
            </motion.div>
          ) : claimed ? (
            <motion.div
              key="claimed"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {/* Claimed reward display */}
              <div className="bg-success/20 rounded-xl p-4 mb-4">
                <motion.p
                  initial={{ y: 10 }}
                  animate={{ y: 0 }}
                  className="text-success font-bold text-lg mb-2"
                >
                  Reward Claimed!
                </motion.p>
                <div className="flex justify-center gap-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: 'spring' }}
                    className="flex items-center gap-2"
                  >
                    <Gem className="text-gem" size={24} />
                    <span className="font-bold text-xl">+{claimedReward?.gems}</span>
                  </motion.div>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="flex items-center gap-2"
                  >
                    <Zap className="text-accent" size={24} />
                    <span className="font-bold text-xl">+{claimedReward?.xp} XP</span>
                  </motion.div>
                </div>
              </div>

              <Button onClick={onClose} size="lg" fullWidth variant="ghost">
                Continue
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="already-claimed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="bg-bg-surface rounded-xl p-4 mb-4">
                <p className="text-text-secondary">
                  You've already claimed today's reward!
                </p>
                <p className="text-sm text-text-muted mt-1">
                  Come back tomorrow for more rewards 💝
                </p>
              </div>

              <Button onClick={onClose} size="lg" fullWidth variant="ghost">
                Close
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  )
}
